import { useState, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@/services/api';
import SearchBar from '@/components/SearchBar';
import type { RestaurantGroup, RestaurantGroupCreateRequest, RestaurantGroupUpdateRequest } from '@/types/api';
import { Plus, Building2, ChevronRight, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';

export default function GroupsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RestaurantGroup | null>(null);

  const canCreate = useHasPermission(PERM.GROUP_CREATE);

  const { data: groups, isLoading, isError, refetch } = useQuery({
    queryKey: ['groups'],
    queryFn: () => apiClient.getGroups(),
  });

  const filteredGroups = useMemo(() => {
    if (!groups) return [];

    return groups.filter((group) =>
      searchQuery === '' ||
      group.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [groups, searchQuery]);

  const openCreateModal = () => {
    setEditingGroup(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingGroup(null);
    setIsModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load restaurant groups</div>
        <button onClick={() => refetch()} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restaurant Groups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage multi-location restaurant chains and franchises
          </p>
        </div>
        {canCreate && (
          <button onClick={openCreateModal} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            New Group
          </button>
        )}
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="max-w-md">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search groups..."
          />
        </div>
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredGroups.length} of {groups?.length || 0} groups
        </div>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredGroups.map((group) => (
          <GroupCard key={group.id} group={group} />
        ))}

        {/* Create New Group Card */}
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="card p-6 border-2 border-dashed border-gray-300 hover:border-primary-400 hover:bg-primary-50 transition-colors flex flex-col items-center justify-center min-h-[200px] text-gray-500 hover:text-primary-600"
          >
            <Plus className="h-10 w-10 mb-3" />
            <span className="font-medium">Create New Group</span>
            <span className="text-sm mt-1">Get Started</span>
          </button>
        )}
      </div>

      {filteredGroups.length === 0 && groups && groups.length > 0 && (
        <div className="text-center py-12 card">
          <p className="text-gray-500">No groups match your search</p>
          <button
            onClick={() => setSearchQuery('')}
            className="btn btn-outline mt-4 h-10"
          >
            Clear Search
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <GroupModal
          group={editingGroup}
          onClose={closeModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
            closeModal();
          }}
        />
      )}
    </div>
  );
}

interface GroupCardProps {
  group: RestaurantGroup;
}

function GroupCard({ group }: GroupCardProps) {
  return (
    <div className="card p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
          {group.description && (
            <p className="text-sm text-gray-500 mt-1">{group.description}</p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <Building2 className="h-4 w-4 mr-2 text-gray-400" />
          {group.restaurantCount ?? 0} restaurant{(group.restaurantCount ?? 0) !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Action */}
      <Link
        to={`/groups/${group.id}`}
        className="btn btn-outline w-full h-10 flex items-center justify-center"
      >
        View Group
        <ChevronRight className="h-4 w-4 ml-1" />
      </Link>
    </div>
  );
}

// Group Modal Component
interface GroupModalProps {
  group: RestaurantGroup | null;
  onClose: () => void;
  onSuccess: () => void;
}

function GroupModal({ group, onClose, onSuccess }: GroupModalProps) {
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    addressLine1: group?.addressLine1 || '',
    addressLine2: group?.addressLine2 || '',
    city: group?.city || '',
    state: group?.state || '',
    zipCode: group?.zipCode || '',
    country: group?.country || '',
    billingAddressLine1: group?.billingAddressLine1 || '',
    billingAddressLine2: group?.billingAddressLine2 || '',
    billingCity: group?.billingCity || '',
    billingState: group?.billingState || '',
    billingZipCode: group?.billingZipCode || '',
    billingCountry: group?.billingCountry || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nameTaken, setNameTaken] = useState(false);

  // Live name-availability check (M1), mirroring RolesPage's checkRoleNameExists
  // pattern. Name is immutable once a group exists (RestaurantGroupUpdateRequest
  // has no name field), so this only applies to creation.
  const checkNameAvailability = async () => {
    const name = formData.name;
    if (group || !name.trim()) {
      setNameTaken(false);
      return;
    }
    try {
      setNameTaken(await apiClient.checkGroupNameExists(name));
    } catch {
      setNameTaken(false); // backend re-validates on submit anyway
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: RestaurantGroupCreateRequest) => apiClient.createGroup(data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create group';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: RestaurantGroupUpdateRequest) => apiClient.updateGroup(group!.id, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update group';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || nameTaken) {
      return;
    }

    if (group) {
      await updateMutation.mutateAsync(formData);
    } else {
      await createMutation.mutateAsync(formData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {group ? 'Edit Group' : 'Create Restaurant Group'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="group-name" className="block text-sm font-medium text-gray-700 mb-1">
              Group Name *
            </label>
            <input
              id="group-name"
              type="text"
              value={formData.name}
              onChange={(e) => {
                setNameTaken(false);
                setFormData({ ...formData, name: e.target.value });
              }}
              onBlur={checkNameAvailability}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Mario's Pizza Group"
              disabled={!!group}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            {nameTaken && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                A group with this name already exists.
              </p>
            )}
            {group && (
              <p className="mt-1 text-xs text-gray-500">Group name cannot be changed after creation.</p>
            )}
          </div>

          <div>
            <label htmlFor="group-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="group-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={2}
              placeholder="Optional description..."
            />
          </div>

          {/* Address */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="group-address1" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  id="group-address1"
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  className="input w-full"
                  placeholder="123 High Street"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="group-address2" className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2
                </label>
                <input
                  id="group-address2"
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="group-city" className="block text-sm font-medium text-gray-700 mb-1">
                  City / Town
                </label>
                <input
                  id="group-city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input w-full"
                  placeholder="London"
                />
              </div>

              <div>
                <label htmlFor="group-state" className="block text-sm font-medium text-gray-700 mb-1">
                  County
                </label>
                <input
                  id="group-state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="input w-full"
                  placeholder="Greater London"
                />
              </div>

              <div>
                <label htmlFor="group-zipcode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode
                </label>
                <input
                  id="group-zipcode"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  className="input w-full"
                  placeholder="SW1A 1AA"
                />
              </div>

              <div>
                <label htmlFor="group-country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  id="group-country"
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="input w-full"
                  placeholder="United Kingdom"
                />
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Billing Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="group-billing-address1" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  id="group-billing-address1"
                  type="text"
                  value={formData.billingAddressLine1}
                  onChange={(e) => setFormData({ ...formData, billingAddressLine1: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="group-billing-address2" className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2
                </label>
                <input
                  id="group-billing-address2"
                  type="text"
                  value={formData.billingAddressLine2}
                  onChange={(e) => setFormData({ ...formData, billingAddressLine2: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="group-billing-city" className="block text-sm font-medium text-gray-700 mb-1">
                  City / Town
                </label>
                <input
                  id="group-billing-city"
                  type="text"
                  value={formData.billingCity}
                  onChange={(e) => setFormData({ ...formData, billingCity: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="group-billing-state" className="block text-sm font-medium text-gray-700 mb-1">
                  County
                </label>
                <input
                  id="group-billing-state"
                  type="text"
                  value={formData.billingState}
                  onChange={(e) => setFormData({ ...formData, billingState: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="group-billing-zipcode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode
                </label>
                <input
                  id="group-billing-zipcode"
                  type="text"
                  value={formData.billingZipCode}
                  onChange={(e) => setFormData({ ...formData, billingZipCode: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="group-billing-country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  id="group-billing-country"
                  type="text"
                  value={formData.billingCountry}
                  onChange={(e) => setFormData({ ...formData, billingCountry: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : group
                ? 'Update Group'
                : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
